/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import {AssetDetailsSectionAccordion} from '@ion/dls/src/components/assetdetails/current/AssetDetailsSectionAccordion';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {dlsColor, dlsLayout, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {AssetDetailsSection, useRegisteredAccordion} from '../../assetDetailsAccordionStore';

export interface DeviceSensorAccordionProps {
  title: string;
  children: React.ReactNode;
  isLoading?: boolean;
  // true only for the Asset Details Drawer's own SC sensor graphs - gates the
  // current theme's section card and accordion-store registration to that
  // drawer, so the Ion Tag details panel keeps its legacy-styled accordion
  // wrapper in current theme too ...
  isAssetDetailsCard?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const padding = dlsLayout('gutters', 'mobile');
  return {
    content : {
      paddingLeft  : padding,
      paddingRight : padding + 10,
      paddingTop   : 0
    },
    deviceDetails : {
      ...dlsTypography('panelCards'),
      fontFamily    : 'Roboto',
      fontWeight    : '600',
      fontSize      : '14px',
      lineHeight    : '17px',
      textTransform : 'uppercase',
      color         : dlsColor('black'),
      minHeight     : 48
    },
    ionTagCardListContentWrapper : {
      padding : '0px 24px'
    },
    detailsRoot : {
      display : 'block',
      padding : '0px'
    },
    DeviceSensorAccordion : {
      padding : '0 15px'
    },
    accordionRoot : {
      borderBottom : `1px solid ${dlsColor('grey5')}`
    },
    loaderContainer : {
      display        : 'flex',
      flexDirection  : 'row',
      alignItems     : 'center',
      justifyContent : 'center',
      height         : 300
    }
  };
});

export const SensorAccordion = ({
  title,
  children,
  isLoading = false,
  isAssetDetailsCard = false
}: DeviceSensorAccordionProps) => {
  const classes              = useStyles();
  const showCurrentAccordion = isCurrentTheme() && isAssetDetailsCard;
  // registers this graph with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // the Asset Details Drawer's current theme card only, the legacy Accordion
  // below (also used by the Ion Tag details panel in current theme) stays
  // uncontrolled ...
  const {expanded, onExpand} = useRegisteredAccordion(
    `${AssetDetailsSection.scSensor}.${title}`,
    false,
    showCurrentAccordion
  );
  const details = !isLoading ? children : (
    <div className={classes.loaderContainer}>
      <IonSpinner />
    </div>
  );

  /**
 * SensorAccordion is a component used for displaying current data being hovered over in a chart/graph.
 *
 * ```typescript
 *  import {SensorAccordion} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 *   <SensorAccordion title={title}
 *    isLoading={loading}>
 *    ... accordion contents
 *   </SensorAccordion>
 */
  return showCurrentAccordion ? (
    <AssetDetailsSectionAccordion title={translate(`tracking:accordionTitle.${title}`)}
      expanded={expanded}
      onExpand={onExpand}>
      {details}
    </AssetDetailsSectionAccordion>
  ) : (
    <div className={classes.accordionRoot}>
      <Accordion className={classes.DeviceSensorAccordion}
        header={
          <div className={classes.deviceDetails}>
            {translate(`tracking:accordionTitle.${title}`)}
          </div>
        }
        details={details}
        padding={'15px 0'}
        borderBottom={'none'}
        isCondensedVariant
        defaultExpanded={false} />
    </div>
  );
};

export default memo(SensorAccordion);
